import get from 'lodash.get';

import { getPluginConfig } from '~/utils/get-plugin-configuration.ts';
import BrightcovePlayer from '~/services/plugins/brightcove-player/components/index.client.tsx';
import VideojsPlayer from '~/components/player/index.client.tsx';
import youboraAnalyticsManifest from '~/services/plugins/YouboraAnalytics/config/manifest.ts';
import jwAnalyticsManifest from '~/services/plugins/jw_analytics/config/manifest.ts';
import muxAnalyticsManifest from '~/services/plugins/mux-analytics/config/manifest.ts';
import continueWatchingManifest from '~/services/plugins/continue-watching/config/manifest.ts';
import defaultPlayerManifest from '~/services/plugins/player/manifest.ts';
import brightcovePlayerManifest from '~/services/plugins/brightcove-player/manifest.ts';

import type { GetContextKeys } from '~/services/pipes/get-context-keys.ts';
import type { PlayerScreenType, Screen, ScreenType } from '~/services/layout';

export function getYouboraAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: youboraAnalytics } = getPluginConfig<
    typeof youboraAnalyticsManifest
  >(youboraAnalyticsManifest, pluginConfigurations);

  const { account_code, user_identifier_token, cdn } = youboraAnalytics;

  const userId =
    get(contextKeys, user_identifier_token as keyof GetContextKeys) || '-1';

  return { account_code, userId, cdn };
}

export function getJWAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: jwAnalytics } = getPluginConfig<
    typeof jwAnalyticsManifest
  >(jwAnalyticsManifest, pluginConfigurations);

  // Add check for JW analytic plugin and pass data to player client
  const { jw_token, jw_user_identity_storage_key } = jwAnalytics;

  const jwUserId =
    get(contextKeys, jw_user_identity_storage_key as keyof GetContextKeys) ||
    '-1';

  return { jw_token, jwUserId };
}

export function getMuxAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: muxAnalytics } = getPluginConfig<
    typeof muxAnalyticsManifest
  >(muxAnalyticsManifest, pluginConfigurations);

  const muxUserId: string =
    get(
      contextKeys,
      muxAnalytics?.user_identity_storage_key as keyof GetContextKeys
    ) || '-1';

  return { muxUserId, muxAnalytics };
}

export function getContinueWatchingPluginData(pluginConfigurations: any) {
  const { customConfiguration: cw } = getPluginConfig<
    typeof continueWatchingManifest
  >(continueWatchingManifest, pluginConfigurations);

  return { cw, continueWatchingSyncedFeed: cw?.synced_feed };
}

const playerTypeToManifest = {
  QuickBrickPlayerPlugin: defaultPlayerManifest,
  'quick-brick-player-brightcove': brightcovePlayerManifest,
};

export function getPlayerPluginConfig(screen?: Screen) {
  const screenType = (screen?.type ||
    'QuickBrickPlayerPlugin') as PlayerScreenType;
  const manifest = playerTypeToManifest[screenType];

  return getPluginConfig<typeof manifest>(manifest, screen);
}

const playerTypeToComponent = {
  QuickBrickPlayerPlugin: VideojsPlayer,
  'quick-brick-player-brightcove': BrightcovePlayer,
};

export function getPlayerComponent(screenType?: ScreenType) {
  if (!screenType) {
    return VideojsPlayer;
  }

  const component = playerTypeToComponent[screenType as PlayerScreenType];

  if (!component) {
    return VideojsPlayer;
  }

  return component;
}
